import { Eye, EyeOff, Pencil, Plus, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  DataTable,
  FormField,
  Input,
  Modal,
  Select,
  Skeleton,
  type DataTableColumn,
} from '@/components/ui';
import { useNotify } from '@/hooks/useNotify';
import { useT } from '@/i18n';
import { api, type AdminPlan, type BandwidthTier } from '@/lib/api';
import { formatPlanBandwidth, formatPrice } from '@/lib/format';

/**
 * Tarifverwaltung.
 *
 * Wichtig fuers Verstaendnis: der Preis einer laufenden Instanz ist zum
 * Bestellzeitpunkt eingefroren. Preisaenderungen hier betreffen nur neue
 * Bestellungen und kuenftige Uplink-Wechsel - Bestandskunden zahlen unveraendert.
 */
export function AdminPlansPage() {
  const t = useT();
  const notify = useNotify();

  const [plans, setPlans] = useState<AdminPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [editTarget, setEditTarget] = useState<AdminPlan | 'new' | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminPlan | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await api.admin.plans();
      setPlans(data.plans);
    } catch (cause) {
      notify.fromError(cause, t('errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [notify, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const togglePublic = async (plan: AdminPlan) => {
    try {
      await api.admin.updatePlan(plan.id, { isPublic: !plan.isPublic });
      notify.success(
        plan.isPublic ? t('plansAdmin.hidden', { name: plan.name }) : t('plansAdmin.shown', { name: plan.name }),
      );
      await load();
    } catch (cause) {
      notify.fromError(cause, t('errors.saveFailed'));
    }
  };

  const remove = async (plan: AdminPlan) => {
    setBusy(true);
    try {
      await api.admin.deletePlan(plan.id);
      notify.success(t('plansAdmin.deleted', { name: plan.name }));
      setDeleteTarget(null);
      await load();
    } catch (cause) {
      notify.fromError(cause, t('errors.deleteFailed'));
    } finally {
      setBusy(false);
    }
  };

  const columns: DataTableColumn<AdminPlan>[] = [
    {
      key: 'name',
      header: t('plansAdmin.plan'),
      render: (row) => (
        <div className="min-w-0">
          <p className="flex items-center gap-2 truncate font-bold text-t0">
            {row.name}
            {!row.isPublic && <Badge variant="neutral">{t('plansAdmin.hiddenBadge')}</Badge>}
          </p>
          <p className="truncate font-mono text-[11.5px] text-t2">{row.slug}</p>
        </div>
      ),
    },
    {
      key: 'price',
      header: t('plansAdmin.price'),
      render: (row) => (
        <span className="font-semibold tabular-nums text-t0">
          {formatPrice(row.priceCents, row.currency)}
          {t('common.perMonth')}
        </span>
      ),
    },
    {
      key: 'limits',
      header: t('plansAdmin.limits'),
      hideBelow: 'md',
      render: (row) => (
        <div className="text-[12px] text-t1">
          <p>{t('plansAdmin.peersCount', { count: row.maxPeers })}</p>
          <p className="text-t2">{formatPlanBandwidth(row.bandwidthLimitMbps)}</p>
        </div>
      ),
    },
    {
      key: 'features',
      header: t('plansAdmin.features'),
      hideBelow: 'lg',
      render: (row) => (
        <div className="flex flex-wrap gap-1">
          {row.ipMode === 'DEDICATED_IPV4' && <Badge variant="accent">{t('instances.dedicated')}</Badge>}
          {row.allowSiteToSite && <Badge variant="info">Site-to-Site</Badge>}
          {row.bandwidthTiers.length > 0 && (
            <Badge variant="neutral">{t('plansAdmin.tierCount', { count: row.bandwidthTiers.length })}</Badge>
          )}
        </div>
      ),
    },
    {
      key: 'instances',
      header: t('nav.instances'),
      render: (row) => <span className="tabular-nums text-t1">{row.instanceCount}</span>,
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '120px',
      render: (row) => (
        <div className="flex justify-end gap-1" onClick={(event) => event.stopPropagation()}>
          <IconButton title={row.isPublic ? t('plansAdmin.hide') : t('plansAdmin.show')} onClick={() => void togglePublic(row)}>
            {row.isPublic ? <Eye size={14} /> : <EyeOff size={14} className="text-warn" />}
          </IconButton>
          <IconButton title={t('common.save')} onClick={() => setEditTarget(row)}>
            <Pencil size={14} />
          </IconButton>
          <IconButton
            title={t('common.delete')}
            danger
            disabled={row.instanceCount > 0}
            onClick={() => setDeleteTarget(row)}
          >
            <Trash2 size={14} />
          </IconButton>
        </div>
      ),
    },
  ];

  if (loading) return <Skeleton className="h-64" />;

  return (
    <div>
      <Card padding="none">
        <div className="flex items-center justify-between border-b border-line px-5 py-4">
          <div>
            <p className="text-[15px] font-bold text-t0">{t('plansAdmin.title')}</p>
            <p className="text-[12px] text-t2">{t('plansAdmin.frozenHint')}</p>
          </div>
          <Button size="sm" icon={<Plus size={14} />} onClick={() => setEditTarget('new')}>
            {t('plansAdmin.create')}
          </Button>
        </div>
        <DataTable columns={columns} data={plans} rowKey={(row) => row.id} />
      </Card>

      {editTarget && (
        <PlanDialog
          plan={editTarget === 'new' ? null : editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={async () => {
            setEditTarget(null);
            notify.success(t('plansAdmin.saved'));
            await load();
          }}
        />
      )}

      <Modal
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        title={t('plansAdmin.deleteTitle')}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDeleteTarget(null)} disabled={busy}>
              {t('common.cancel')}
            </Button>
            <Button variant="danger" disabled={busy} onClick={() => deleteTarget && void remove(deleteTarget)}>
              {t('common.delete')}
            </Button>
          </div>
        }
      >
        <p className="text-[13.5px] leading-relaxed text-t1">
          {t('plansAdmin.deleteText', { name: deleteTarget?.name ?? '' })}
        </p>
      </Modal>
    </div>
  );
}

function IconButton({
  title,
  danger,
  disabled,
  onClick,
  children,
}: {
  title: string;
  danger?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      disabled={disabled}
      onClick={onClick}
      className={`flex h-8 w-8 items-center justify-center rounded-[var(--radius-vela-sm)] hover:bg-bg-3 disabled:opacity-30 ${
        danger ? 'text-bad' : 'text-t1'
      }`}
    >
      {children}
    </button>
  );
}

/** Betrag in Euro als Text -> Cent. */
function toCents(value: string): number {
  const parsed = Number.parseFloat(value.replace(',', '.'));
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0;
}

function fromCents(cents: number): string {
  return (cents / 100).toFixed(2);
}

function PlanDialog({
  plan,
  onClose,
  onSaved,
}: {
  plan: AdminPlan | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const t = useT();

  const [slug, setSlug] = useState(plan?.slug ?? '');
  const [name, setName] = useState(plan?.name ?? '');
  const [description, setDescription] = useState(plan?.description ?? '');
  const [maxPeers, setMaxPeers] = useState(plan?.maxPeers ?? 16);
  const [bandwidth, setBandwidth] = useState(plan?.bandwidthLimitMbps ?? 120);
  const [ipMode, setIpMode] = useState<AdminPlan['ipMode']>(plan?.ipMode ?? 'SHARED_IPV4');
  const [siteToSite, setSiteToSite] = useState(plan?.allowSiteToSite ?? false);
  const [price, setPrice] = useState(fromCents(plan?.priceCents ?? 0));
  const [sortOrder, setSortOrder] = useState(plan?.sortOrder ?? 100);
  const [isPublic, setIsPublic] = useState(plan?.isPublic ?? true);
  const [tiers, setTiers] = useState<BandwidthTier[]>(plan?.bandwidthTiers ?? []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setTier = (index: number, patch: Partial<BandwidthTier>) => {
    setTiers((current) => current.map((tier, i) => (i === index ? { ...tier, ...patch } : tier)));
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);

    const payload = {
      slug: slug.trim(),
      name: name.trim(),
      description: description.trim() || undefined,
      maxPeers,
      bandwidthLimitMbps: bandwidth,
      ipMode,
      allowSiteToSite: siteToSite,
      priceCents: toCents(price),
      sortOrder,
      isPublic,
      bandwidthTiers: tiers.filter((tier) => tier.mbps > 0),
      prioritySupport: false,
    };

    try {
      if (plan) await api.admin.updatePlan(plan.id, payload);
      else await api.admin.createPlan(payload);
      await onSaved();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('errors.saveFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={plan ? t('plansAdmin.editTitle', { name: plan.name }) : t('plansAdmin.create')}
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField label={t('plansAdmin.name')} required>
            <Input required maxLength={80} value={name} onChange={(event) => setName(event.target.value)} />
          </FormField>
          <FormField label="Slug" required hint={t('plansAdmin.slugHint')}>
            <Input
              required
              className="font-mono text-xs"
              pattern="[a-z0-9-]+"
              maxLength={60}
              value={slug}
              onChange={(event) => setSlug(event.target.value)}
            />
          </FormField>
        </div>

        <FormField label={t('plansAdmin.description')}>
          <Input maxLength={300} value={description} onChange={(event) => setDescription(event.target.value)} />
        </FormField>

        <div className="grid gap-4 sm:grid-cols-4">
          <FormField label={t('plansAdmin.priceField')} required>
            <Input
              required
              inputMode="decimal"
              value={price}
              onChange={(event) => setPrice(event.target.value)}
            />
          </FormField>
          <FormField label="Peers" required>
            <Input
              type="number"
              min={1}
              max={4096}
              required
              value={maxPeers}
              onChange={(event) => setMaxPeers(Number(event.target.value))}
            />
          </FormField>
          <FormField label={t('plansAdmin.baseBandwidth')} required>
            <Input
              type="number"
              min={1}
              max={100000}
              required
              value={bandwidth}
              onChange={(event) => setBandwidth(Number(event.target.value))}
            />
          </FormField>
          <FormField label={t('plansAdmin.sortOrder')}>
            <Input
              type="number"
              min={0}
              max={1000}
              value={sortOrder}
              onChange={(event) => setSortOrder(Number(event.target.value))}
            />
          </FormField>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <FormField label={t('plansAdmin.ipMode')}>
            <Select value={ipMode} onChange={(event) => setIpMode(event.target.value as AdminPlan['ipMode'])}>
              <option value="SHARED_IPV4">{t('instances.shared')}</option>
              <option value="DEDICATED_IPV4">{t('instances.dedicated')}</option>
            </Select>
          </FormField>
          <div className="flex flex-col justify-end gap-2 pb-1">
            <Checkbox
              checked={siteToSite}
              onChange={(event) => setSiteToSite(event.target.checked)}
              label="Site-to-Site"
            />
            <Checkbox
              checked={isPublic}
              onChange={(event) => setIsPublic(event.target.checked)}
              label={t('plansAdmin.isPublic')}
            />
          </div>
        </div>

        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <p className="text-[12.5px] font-semibold text-t1">{t('plansAdmin.tiers')}</p>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              icon={<Plus size={13} />}
              onClick={() => setTiers((current) => [...current, { mbps: 0, priceCents: 0 }])}
            >
              {t('plansAdmin.addTier')}
            </Button>
          </div>
          <p className="mb-2 text-[12px] text-t2">{t('plansAdmin.tiersHint')}</p>
          <div className="space-y-2">
            {tiers.map((tier, index) => (
              <div key={index} className="flex items-center gap-2">
                <Input
                  type="number"
                  min={1}
                  placeholder="Mbit/s"
                  className="w-32"
                  value={tier.mbps || ''}
                  onChange={(event) => setTier(index, { mbps: Number(event.target.value) })}
                />
                <span className="text-[12px] text-t2">Mbit/s · +</span>
                <Input
                  inputMode="decimal"
                  placeholder="0,00"
                  className="w-28"
                  value={fromCents(tier.priceCents)}
                  onChange={(event) => setTier(index, { priceCents: toCents(event.target.value) })}
                />
                <span className="text-[12px] text-t2">{t('common.perMonth')}</span>
                <IconButton
                  title={t('common.delete')}
                  danger
                  onClick={() => setTiers((current) => current.filter((_, i) => i !== index))}
                >
                  <Trash2 size={13} />
                </IconButton>
              </div>
            ))}
          </div>
        </div>

        {error && (
          <div className="rounded-[var(--radius-vela-md)] border border-line bg-bad-soft px-3 py-2 text-[13px] text-bad">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={busy}>
            {t('common.save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
